import {
  CardContent,
  Typography,
  Grid,
  Box,
  makeStyles,
} from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useAsync } from 'react-use';
import { InfoCard } from '@backstage/core-components';

const useStyles = makeStyles(theme => ({
  overviewCard: {
    height: '100%',
  },
  // Green theme for Products
  greenMetricCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#2e7d32', // Dark green background
    borderRadius: theme.shape.borderRadius,
    border: '2px solid #4caf50',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  greenMetricNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#ffffff', // White text
    marginBottom: theme.spacing(1),
  },
  // Blue theme for Components
  blueMetricCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#1565c0', // Dark blue background
    borderRadius: theme.shape.borderRadius,
    border: '2px solid #2196f3',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  blueMetricNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#ffffff', // White text
    marginBottom: theme.spacing(1),
  },
  // Light blue theme for Teams/Groups
  lightBlueMetricCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    backgroundColor: '#00838f', // Dark teal background
    borderRadius: theme.shape.borderRadius,
    border: '2px solid #00bcd4',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  lightBlueMetricNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    color: '#ffffff', // White text
    marginBottom: theme.spacing(1),
  },
  metricLabel: {
    color: '#ffffff', // White text for dark backgrounds
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
}));

export const OverviewCard = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  // Fetch products (assuming they are System entities)
  const { value: products, loading: productsLoading, error: productsError } = useAsync(() =>
    catalogApi.getEntities({
      filter: {
        kind: 'System',
      },
    }),
  );

  // Fetch components
  const { value: components, loading: componentsLoading, error: componentsError } = useAsync(() =>
    catalogApi.getEntities({
      filter: {
        kind: 'Component',
      },
    }),
  );

  // Fetch teams/groups (focusing on Group entities primarily)
  const { value: teams, loading: teamsLoading, error: teamsError } = useAsync(() =>
    catalogApi.getEntities({
      filter: {
        kind: 'Group',
      },
    }),
  );

  const isLoading = productsLoading || componentsLoading || teamsLoading;
  const hasError = productsError || componentsError || teamsError;

  const productCount = products?.items?.length || 0;
  const componentCount = components?.items?.length || 0;
  const teamCount = teams?.items?.length || 0;

  if (hasError) {
    return (
      <InfoCard title="Overview" className={classes.overviewCard}>
        <CardContent>
          <Typography color="error">
            Failed to load overview data. Please try refreshing the page.
          </Typography>
        </CardContent>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Overview" className={classes.overviewCard}>
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Box className={classes.greenMetricCard}>
              <Typography className={classes.greenMetricNumber}>
                {isLoading ? '...' : productCount}
              </Typography>
              <Typography className={classes.metricLabel}>
                Products
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box className={classes.blueMetricCard}>
              <Typography className={classes.blueMetricNumber}>
                {isLoading ? '...' : componentCount}
              </Typography>
              <Typography className={classes.metricLabel}>
                Components
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Box className={classes.lightBlueMetricCard}>
              <Typography className={classes.lightBlueMetricNumber}>
                {isLoading ? '...' : teamCount}
              </Typography>
              <Typography className={classes.metricLabel}>
                Teams/Groups
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </InfoCard>
  );
};